import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { NotificationSettings } from "@/components/PwaControls";
import { UploadCloud, Save, Palette, Users, UserPlus, QrCode } from "lucide-react";

const field = "w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const label = "text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block";
const PRESETS = ["#124e3f", "#1e4e8c", "#8c2d1e", "#5b2d8c", "#8c6b1e", "#2d6b4a", "#b8481e"];

export default function Settings() {
  const { org, loadMe } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const logoRef = useRef();
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/org").then(({ data }) => setF({
      name: data.name || "", legal_name: data.legal_name || "", vat: data.vat || "", cf: data.cf || "",
      brand_color: data.brand_color || "#124e3f", logo_url: data.logo_url,
      contacts: data.contacts || { email: "", phone: "", address: "" },
    }));
  }, []);

  useEffect(() => { api.get("/public/sponsors").then(({ data }) => setSponsors(data)).catch(() => {}); }, []);

  if (!f) return null;

  const save = async () => {
    await api.put("/org/branding", { name: f.name, legal_name: f.legal_name, vat: f.vat, cf: f.cf, brand_color: f.brand_color, contacts: f.contacts });
    toast.success("Impostazioni salvate");
    await loadMe();
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/org/logo", fd);
    setF({ ...f, logo_url: data.logo_url });
    toast.success("Logo aggiornato");
    await loadMe();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div><h1 className="font-display text-3xl font-bold tracking-tight">Azienda</h1><p className="text-muted-foreground text-sm mt-1">Branding, dati aziendali e collaboratori</p></div>

      <NotificationSettings />

      <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-6 space-y-5">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Identità visiva</h2>
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-xl border border-border bg-muted grid place-items-center overflow-hidden shrink-0">
            {f.logo_url ? <img src={fileUrl(f.logo_url)} alt="logo" className="h-full w-full object-contain" /> : <span className="text-2xl font-display font-bold text-primary">MP</span>}
          </div>
          <div>
            <button onClick={() => logoRef.current?.click()} className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2" data-testid="tenant-brand-logo-upload"><UploadCloud className="h-4 w-4" /> Carica logo</button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
            <p className="text-xs text-muted-foreground mt-2">PNG o SVG consigliati</p>
          </div>
        </div>
        <div>
          <label className={label}>Colore del brand</label>
          <div className="flex items-center gap-3 flex-wrap" data-testid="tenant-brand-color-picker">
            {PRESETS.map((c) => <button key={c} onClick={() => setF({ ...f, brand_color: c })} style={{ background: c }} className={`h-9 w-9 rounded-full border-2 transition-transform ${f.brand_color === c ? "border-foreground scale-110" : "border-transparent"}`} />)}
            <input type="color" value={f.brand_color} onChange={(e) => setF({ ...f, brand_color: e.target.value })} className="h-9 w-14 rounded border border-border cursor-pointer" />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-6 space-y-4">
        <h2 className="font-display text-lg font-semibold">Dati aziendali</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Nome commerciale</label><input className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className={label}>Ragione sociale</label><input className={field} value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} /></div>
          <div><label className={label}>P.IVA</label><input className={field} value={f.vat} onChange={(e) => setF({ ...f, vat: e.target.value })} /></div>
          <div><label className={label}>Codice Fiscale</label><input className={field} value={f.cf} onChange={(e) => setF({ ...f, cf: e.target.value })} /></div>
          <div><label className={label}>Email</label><input className={field} value={f.contacts.email} onChange={(e) => setF({ ...f, contacts: { ...f.contacts, email: e.target.value } })} /></div>
          <div><label className={label}>Telefono</label><input className={field} value={f.contacts.phone} onChange={(e) => setF({ ...f, contacts: { ...f.contacts, phone: e.target.value } })} /></div>
          <div className="sm:col-span-2"><label className={label}>Indirizzo</label><input className={field} value={f.contacts.address} onChange={(e) => setF({ ...f, contacts: { ...f.contacts, address: e.target.value } })} /></div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-6 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Collaboratori</h2>
          <p className="text-sm text-muted-foreground mt-1">Invita collaboratori e assegna ruoli e permessi (Gestore / Operatore).</p>
        </div>
        <button onClick={() => navigate("/delegates")} className="bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg hover:brightness-95 flex items-center gap-2 shrink-0" data-testid="azienda-collaboratori-link"><UserPlus className="h-4 w-4" /> Gestisci</button>
      </div>

      {sponsors.length > 0 && (
        <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-6 space-y-4" data-testid="company-sponsors">
          <h2 className="font-display text-lg font-semibold">Sponsor</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {sponsors.map((s) => (
              <div key={s.id} className="border border-border/70 rounded-xl p-4 flex gap-4" data-testid={`company-sponsor-${s.id}`}>
                <div className="flex-1 min-w-0">
                  {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-10 object-contain mb-2" /> : <div className="font-display font-bold">{s.name}</div>}
                  <div className="font-semibold text-sm">{s.title || s.name}</div>
                  {s.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{s.description}</p>}
                </div>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/sponsor-qr/${s.id}?v=${encodeURIComponent(s.updated_at || "")}`} alt="QR" className="h-20 w-20 rounded-lg border border-border shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={save} className="bg-[#E3FF00] text-neutral-900 font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-95" data-testid="settings-save-button"><Save className="h-4 w-4" /> Salva impostazioni</button>
    </div>
  );
}
